import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

// Floating particle field that reacts to scroll + mouse
function Particles({ scrollRef }) {
  const pointsRef = useRef();
  const { viewport } = useThree();

  const { positions } = useMemo(() => {
    const count = 900;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 28;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 28;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 18;
    }
    return { positions };
  }, []);

  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    const scroll = scrollRef.current || 0;
    if (pointsRef.current) {
      pointsRef.current.rotation.y = t * 0.04 + scroll * 0.0006;
      pointsRef.current.rotation.x = Math.sin(t * 0.15) * 0.12 + scroll * 0.0002;
      // Gentle mouse parallax
      pointsRef.current.position.x = THREE.MathUtils.lerp(
        pointsRef.current.position.x,
        (state.mouse.x * viewport.width) * 0.03,
        0.04
      );
      pointsRef.current.position.y = THREE.MathUtils.lerp(
        pointsRef.current.position.y,
        (state.mouse.y * viewport.height) * 0.03,
        0.04
      );
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial
        size={0.06}
        color="#2563eb"
        transparent
        opacity={0.55}
        sizeAttenuation
        depthWrite={false}
      />
    </points>
  );
}

// A group of floating wireframe geometry that drifts with scroll
function FloatingShapes({ scrollRef }) {
  const group = useRef();

  const shapes = useMemo(
    () => [
      { pos: [-6, 3, -4], scale: 1.4, geo: 'icosa', speed: 0.3 },
      { pos: [7, -2, -6], scale: 1.9, geo: 'octa', speed: -0.24 },
      { pos: [6, 4, -8], scale: 1.1, geo: 'torus', speed: 0.4 },
      { pos: [-7, -4, -5], scale: 1.5, geo: 'octa', speed: 0.2 },
      { pos: [2, -1, -14], scale: 2.6, geo: 'icosa', speed: -0.16 },
    ],
    []
  );

  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    const scroll = scrollRef.current || 0;
    if (group.current) {
      group.current.children.forEach((mesh, i) => {
        const s = shapes[i];
        mesh.rotation.x = t * s.speed;
        mesh.rotation.y = t * s.speed * 0.7;
        mesh.position.y = s.pos[1] + Math.sin(t * 0.5 + i) * 0.6 - scroll * 0.004 * (i + 1);
      });
    }
  });

  return (
    <group ref={group}>
      {shapes.map((s, i) => (
        <mesh key={i} position={s.pos} scale={s.scale}>
          {s.geo === 'icosa' && <icosahedronGeometry args={[1, 0]} />}
          {s.geo === 'octa' && <octahedronGeometry args={[1, 0]} />}
          {s.geo === 'torus' && <torusGeometry args={[0.8, 0.28, 12, 32]} />}
          <meshStandardMaterial
            color={i % 2 === 0 ? '#06b6d4' : '#2563eb'}
            wireframe
            transparent
            opacity={0.22}
          />
        </mesh>
      ))}
    </group>
  );
}

const Scene3D = ({ scrollRef }) => {
  return (
    <Canvas
      camera={{ position: [0, 0, 10], fov: 60 }}
      dpr={[1, 2]}
      gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
      style={{ position: 'fixed', inset: 0, width: '100%', height: '100%' }}
    >
      <ambientLight intensity={0.7} />
      <pointLight position={[10, 10, 10]} intensity={1.2} color="#22d3ee" />
      <pointLight position={[-10, -10, -5]} intensity={0.8} color="#3b82f6" />
      <Particles scrollRef={scrollRef} />
      <FloatingShapes scrollRef={scrollRef} />
      <fog attach="fog" args={['#eef4ff', 12, 26]} />
    </Canvas>
  );
};

export default Scene3D;
